import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { cn } from '@/utils/cn';
import { Avatar } from '../ui/Avatar';
import { Logo } from './Logo';

const desktopLink = ({ isActive }: { isActive: boolean }) =>
  cn('whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-brand-50 text-brand-800' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900');

const mobileLink = ({ isActive }: { isActive: boolean }) =>
  cn('block rounded-lg px-3 py-3 text-base font-medium transition-colors',
    isActive ? 'bg-brand-50 text-brand-800' : 'text-slate-700 hover:bg-slate-100');

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}

/**
 * Application frame. One list of links drives both layouts: inline from `md` up, a
 * disclosure panel below it. Two hand-written navs would drift apart, and on a phone
 * the account links (including Log out) must be reachable.
 */
export function AppShell() {
  const { status, user, isAdmin, signOut } = useAuth();
  const signedIn = status === 'authenticated';
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const primary: Array<[string, string]> = [['/search', 'Find a ride']];
  if (signedIn) {
    primary.push(['/create-ride', 'Offer a ride'], ['/my-rides', 'My rides'], ['/bookings', 'Bookings']);
  }
  if (isAdmin) primary.push(['/admin', 'Admin']);

  const account: Array<[string, string]> = signedIn
    ? [['/messages', 'Messages'], ['/notifications', 'Alerts'], ['/profile', 'Profile']]
    : [];

  return (
    <div className="flex min-h-full flex-col">
      <a href="#main" className="skip-link">Skip to content</a>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 px-4 sm:gap-4 sm:px-6">
          <Link to="/" aria-label="WeDrive home" className="shrink-0"><Logo /></Link>

          <nav aria-label="Main" className="hidden min-w-0 flex-1 md:block">
            <ul className="flex items-center gap-2">
              {primary.map(([to, label]) => <li key={to}><NavLink to={to} className={desktopLink}>{label}</NavLink></li>)}
            </ul>
          </nav>
          <div className="flex-1 md:hidden" />

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {signedIn ? (
              <>
                <div className="hidden items-center gap-2 md:flex">
                  <NavLink to="/messages" className={desktopLink}>Messages</NavLink>
                  <NavLink to="/notifications" className={desktopLink}>Alerts</NavLink>
                </div>
                <NavLink to="/profile" aria-label="Your profile" className="ml-1">
                  <Avatar name={user?.name ?? '?'} src={user?.photoUrl} size="sm" />
                </NavLink>
                <button onClick={signOut} className="hidden rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 md:block">Log out</button>
              </>
            ) : status === 'anonymous' ? (
              <>
                <Link to="/login" className="hidden rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 sm:block">Log in</Link>
                <Link to="/register" className="rounded-xl bg-brand-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-800">Sign up</Link>
              </>
            ) : null}

            <button
              type="button"
              className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 md:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav id="mobile-menu" aria-label="Mobile" className="border-t border-slate-200 bg-white px-4 pb-4 pt-2 shadow-lg md:hidden">
            <ul className="space-y-1">
              {[...primary, ...account].map(([to, label]) => (
                <li key={to}><NavLink to={to} className={mobileLink}>{label}</NavLink></li>
              ))}
              {signedIn && (
                <li>
                  <button onClick={signOut} className="block w-full rounded-lg px-3 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-100">
                    Log out
                  </button>
                </li>
              )}
              {status === 'anonymous' && (
                <li><NavLink to="/login" className={mobileLink}>Log in</NavLink></li>
              )}
            </ul>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Suspense fallback={<p role="status" className="p-8 text-center text-slate-500">Loading...</p>}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>&copy; {new Date().getFullYear()} WeDrive</p>
          <p>Share the ride, split the cost.</p>
        </div>
      </footer>
    </div>
  );
}
